import axiosClient from './axios';

// ============ Types ============

export interface Expert {
  user_id: number;
  name: string;
  bio: string;
  image: string | null;
  is_verified: boolean;
  is_expert: boolean;
  sebi_qualification: string;
  sebi_reg_number: string;
  user_avg_rating: number;
  languages: string[];
  years_of_xp: number;
  call_session_rate: number;
  chat_session_rate: number;
  company_associated: boolean;
  company_name: string;
}

export interface Booking {
  id: number;
  expert_id: number;
  user_id: number;
  expert: string;
  user: string;
  start_time: string;
  end_time?: string;
  booking_type: 'V' | 'C'; // V = Video, C = Chat
  is_booked: boolean;
  is_completed: boolean;
  is_rated?: boolean;
  expert_agora_id?: string;
  user_agora_id?: string;
}

export interface TimeSlot {
  time: string;
  is_available: boolean;
}

export interface AvailabilityData {
  date: string;
  slots: TimeSlot[];
}

export interface Rating {
  id: number;
  user_name: string;
  user_id: number;
  rating: number;
  feedback: string;
  created_at: string;
}

export interface BookingOrder {
  order_id: string;
  amount: number;
  currency: string;
}

export interface ExpertSearchResponse {
  experts: Expert[];
  total_experts: number;
  page: number;
  total_pages: number;
}

export interface AgoraTokenResponse {
  token: string;
  channel: string;
  uid: number;
  app_id: string;
}

// ============ Expert Search ============

/**
 * Search for experts by query string
 */
export const handleExpertSearch = async (
  query: string = '',
  page: number = 1
): Promise<ExpertSearchResponse> => {
  try {
    const response = await axiosClient.get(`auth/expert_search/`, {
      params: { q: query, page },
    });
    return response.data;
  } catch (error) {
    console.error('Error searching experts:', error);
    throw error;
  }
};

/**
 * Fetch expert details by user ID
 */
export const fetchExpertDetails = async (expertId: number): Promise<Expert> => {
  try {
    const response = await axiosClient.get(`auth/user_details`, {
      params: { id: expertId },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching expert details:', error);
    throw error;
  }
};

// ============ Bookings ============

/**
 * Fetch all bookings for an expert
 */
export const fetchAllBookings = async (
  expertId: number,
  startDate?: string,
  endDate?: string
): Promise<Booking[]> => {
  try {
    const params: Record<string, any> = { expert_id: expertId };
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    const response = await axiosClient.get(`bookings/expert_booking/`, { params });
    return response.data.bookings || response.data;
  } catch (error) {
    console.error('Error fetching bookings:', error);
    throw error;
  }
};

/**
 * Fetch user's bookings
 */
export const fetchUserBookings = async (startDate?: string): Promise<Booking[]> => {
  try {
    const params: Record<string, any> = {};
    if (startDate) params.st = startDate;

    const response = await axiosClient.get(`bookings/user_booking/`, { params });
    return response.data.bookings || response.data;
  } catch (error) {
    console.error('Error fetching user bookings:', error);
    throw error;
  }
};

/**
 * Create a new booking with an expert
 */
export const createExpertBooking = async (
  expertId: number,
  data: {
    start_time: string;
    end_time?: string;
    booking_type: 'V' | 'C';
    razorpay_res?: any;
  }
): Promise<Booking> => {
  try {
    const response = await axiosClient.post(
      `bookings/expert_booking/`,
      data,
      { params: { expert_id: expertId } }
    );
    return response.data;
  } catch (error) {
    console.error('Error creating booking:', error);
    throw error;
  }
};

/**
 * Update an existing booking
 */
export const updateExpertBooking = async (
  bookingId: number,
  data: {
    start_time?: string;
    end_time?: string;
    booking_type?: 'V' | 'C';
  }
): Promise<Booking> => {
  try {
    const response = await axiosClient.put(
      `bookings/expert_booking/`,
      data,
      { params: { id: bookingId } }
    );
    return response.data;
  } catch (error) {
    console.error('Error updating booking:', error);
    throw error;
  }
};

/**
 * Accept a booking (for experts)
 */
export const acceptBooking = async (bookingId: number): Promise<Booking> => {
  try {
    const response = await axiosClient.patch(
      `bookings/expert_booking/`,
      { is_booked: true },
      { params: { id: bookingId } }
    );
    return response.data;
  } catch (error) {
    console.error('Error accepting booking:', error);
    throw error;
  }
};

/**
 * Cancel a booking
 */
export const cancelBooking = async (bookingId: number): Promise<void> => {
  try {
    await axiosClient.delete(`bookings/expert_booking/`, {
      params: { id: bookingId },
    });
  } catch (error) {
    console.error('Error canceling booking:', error);
    throw error;
  }
};

// ============ Availability ============

/**
 * Fetch expert's availability for a specific date
 */
export const fetchExpertAvailability = async (
  expertId: number,
  date: string
): Promise<AvailabilityData> => {
  try {
    const response = await axiosClient.get(`bookings/expert_availability/`, {
      params: { id: expertId, date },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching availability:', error);
    throw error;
  }
};

/**
 * Update expert's availability
 */
export const updateExpertAvailability = async (
  payload: {
    date: string;
    slots: TimeSlot[];
  }
): Promise<AvailabilityData> => {
  try {
    const response = await axiosClient.post(`bookings/expert_availability/`, payload);
    return response.data;
  } catch (error) {
    console.error('Error updating availability:', error);
    throw error;
  }
};

// ============ Ratings ============

/**
 * Fetch ratings for an expert
 */
export const fetchExpertRatings = async (expertId: number): Promise<Rating[]> => {
  try {
    const response = await axiosClient.get(`bookings/expert_rating_feedback/`, {
      params: { id: expertId },
    });
    return response.data.ratings || response.data;
  } catch (error) {
    console.error('Error fetching ratings:', error);
    throw error;
  }
};

/**
 * Submit a rating for an expert
 */
export const submitExpertRating = async (data: {
  id: number; // expert_id
  rating: number;
  feedback: string;
  booking_id: number;
}): Promise<Rating> => {
  try {
    const response = await axiosClient.post(`bookings/expert_rating_feedback/`, data);
    return response.data;
  } catch (error) {
    console.error('Error submitting rating:', error);
    throw error;
  }
};

// ============ Payment ============

/**
 * Create a booking order for payment
 */
export const createBookingOrder = async (
  expertId: number,
  bookingType: 'V' | 'C'
): Promise<BookingOrder> => {
  try {
    const response = await axiosClient.post(`bookings/booking_order/`, {
      expert_id: expertId,
      booking_type: bookingType,
    });
    return response.data.order || response.data;
  } catch (error) {
    console.error('Error creating booking order:', error);
    throw error;
  }
};

// ============ Agora Tokens ============

/**
 * Fetch Agora token for video call
 */
export const fetchAgoraToken = async (bookingId: number): Promise<AgoraTokenResponse> => {
  try {
    const response = await axiosClient.get(`bookings/agora_token/`, {
      params: { id: bookingId },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching Agora token:', error);
    throw error;
  }
};

/**
 * Fetch Agora chat token
 */
export const fetchAgoraChatToken = async (): Promise<AgoraTokenResponse> => {
  try {
    const response = await axiosClient.get(`bookings/agora_chat_token/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching Agora chat token:', error);
    throw error;
  }
};
